import styled, { css } from 'styled-components';
import { glassDeep, glassHover } from './glassmorphism';

/**
 * Common card components with consistent styling
 */

// Base card styles
const cardBase = css`
  ${glassDeep}
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.shadows.card};
  position: relative;
  overflow: hidden;
  
  /* Animated glow effect on hover */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover {
    ${glassHover}
    
    &:before {
      opacity: 1;
    }
  }
`;

// Standard card component
export const Card = styled.div`
  ${cardBase}
`;

// Card with header section
export const CardWithHeader = styled.div`
  ${cardBase}
  padding: 0;
  
  .card-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(99, 102, 241, 0.2);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .card-content {
    padding: 1.5rem;
  }
`;

// Card header component
export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;

  h2, h3 {
    margin: 0;
    color: rgba(255, 255, 255, 0.9);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.75rem;
    left: 0;
    width: 3rem;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
    border-radius: 1px;
  }
`;

// Card footer component
export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
`;

// Interactive card (for items that function as buttons)
export const InteractiveCard = styled.div`
  ${cardBase}
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: ${({ theme }) => theme.shadows.hover};
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

// Card grid layout
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Stats card for dashboard metrics
export const StatsCard = styled.div`
  ${cardBase}
  display: flex;
  flex-direction: column;
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.95);
    margin: 0.5rem 0;
  }
  
  .stat-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
  }
  
  .stat-icon {
    margin-bottom: 1rem;
    color: rgba(99, 102, 241, 0.7);
    font-size: 1.5rem;
  }
`;

// Empty state card
export const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem;
  background: rgba(15, 23, 42, 0.4);
  border-radius: 16px;
  border: 1px dashed rgba(99, 102, 241, 0.3);
  min-height: 300px;
  
  h3 {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 1rem;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1.5rem;
    max-width: 500px;
  }
  
  svg {
    font-size: 4rem;
    color: rgba(99, 102, 241, 0.5);
    margin-bottom: 1.5rem;
  }
`;
